import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import SessionManager from '../../components/ui/SessionManager';
import SmartExpiryRadar from '../../components/ui/SmartExpiryRadar';
import CertificateFilters from './components/CertificateFilters';
import CertificateTable from './components/CertificateTable';
import CertificateDetailsModal from './components/CertificateDetailsModal';
import ReminderEmailModal from './components/ReminderEmailModal';
import UploadDocumentModal from './components/UploadDocumentModal';
import CalendarView from './components/CalendarView';
import BulkActionsBar from './components/BulkActionsBar';
import TableSkeleton from '../../components/ui/TableSkeleton';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { certificatesService } from '../../services/supabaseService';
import toast from 'react-hot-toast';
import { differenceInDays, parseISO } from 'date-fns';

const SmartCertificateExpiryManagement = () => {
  const [viewMode, setViewMode] = useState('table');
  const [filters, setFilters] = useState({
    search: '',
    type: 'all',
    status: 'all',
    contractor: 'all',
  });
  const [certificates, setCertificates] = useState([]);
  const [filteredCertificates, setFilteredCertificates] = useState([]);
  const [selectedCertificates, setSelectedCertificates] = useState([]);
  const [detailsModal, setDetailsModal] = useState({ isOpen: false, certificate: null });
  const [reminderModal, setReminderModal] = useState({ isOpen: false, certificate: null });
  const [uploadModal, setUploadModal] = useState({ isOpen: false, certificate: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCertificates();
  }, []);

  const fetchCertificates = async () => {
    try {
      setLoading(true);
      const data = await certificatesService?.getAll();
      
      // Calculate days until expiry and add property address
      const enrichedData = data?.map(cert => {
        const expiryDate = cert?.expiryDate ? parseISO(cert?.expiryDate) : null;
        const daysUntilExpiry = expiryDate ? differenceInDays(expiryDate, new Date()) : null;
        
        return {
          ...cert,
          daysUntilExpiry,
          address: cert?.properties ? `${cert?.properties?.address1}, ${cert?.properties?.postcode}` : 'N/A',
          typeName: getCertTypeName(cert?.certType),
          hasDocument: !!cert?.fileUrl
        };
      });
      
      setCertificates(enrichedData);
      setFilteredCertificates(enrichedData);
    } catch (error) {
      console.error('Error fetching certificates:', error);
      toast?.error('Failed to load certificates');
    } finally {
      setLoading(false);
    }
  };

  const getCertTypeName = (type) => {
    const typeMap = {
      'gas': 'Gas Safety Certificate',
      'epc': 'Energy Performance Certificate',
      'eicr': 'Electrical Installation Certificate',
      'legionella': 'Legionella Risk Assessment',
      'smoke': 'Smoke Alarm Certificate',
      'other': 'Other Certificate'
    };
    return typeMap?.[type] || type;
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      type: 'all',
      status: 'all',
      contractor: 'all',
    });
  };

  const handleViewDetails = (certificate) => {
    setDetailsModal({ isOpen: true, certificate });
  };

  const handleGenerateReminder = (certificate) => {
    setReminderModal({ isOpen: true, certificate });
  };

  const handleUploadDocument = (certificate) => {
    setUploadModal({ isOpen: true, certificate });
  };

  const handleDateClick = (day, certificates) => {
    console.log('Certificates expiring on day', day, certificates);
  };

  const handleExportData = () => {
    const csvContent = [
      ['Property ID', 'Address', 'Certificate Type', 'Issue Date', 'Expiry Date', 'Status', 'Days Until Expiry'],
      ...filteredCertificates?.map((cert) => [
        cert?.propertyId,
        cert?.address,
        cert?.typeName,
        cert?.issueDate,
        cert?.expiryDate,
        cert?.daysUntilExpiry < 0 ? 'Expired' : cert?.daysUntilExpiry <= 14 ? 'Critical' : cert?.daysUntilExpiry <= 30 ? 'Expiring Soon' : 'Valid',
        cert?.daysUntilExpiry,
      ]),
    ]?.map((row) => row?.join(','))?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `certificates-export-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    a?.click();
  };

  if (loading) {
    return (
      <>
        <Header />
        <SessionManager />
        <div className="min-h-screen bg-background pt-[108px]">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-6 md:mb-8">
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2 font-heading">
                  Smart Certificate Expiry Management
                </h1>
                <p className="text-sm md:text-base text-muted-foreground">
                  Monitor compliance and track certificate renewals across all properties
                </p>
              </div>
            </div>

            <div className="mb-6 md:mb-8 bg-surface rounded-lg border border-border p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="h-24 bg-muted rounded-lg animate-shimmer" />
                <div className="h-24 bg-muted rounded-lg animate-shimmer" style={{ animationDelay: '0.1s' }} />
                <div className="h-24 bg-muted rounded-lg animate-shimmer" style={{ animationDelay: '0.2s' }} />
              </div>
            </div>

            <div className="bg-surface rounded-lg border border-border p-6">
              <TableSkeleton rows={10} columns={7} />
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <SessionManager />
      <div className="min-h-screen bg-background pt-[108px]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-6 md:mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2 font-heading">
                Smart Certificate Expiry Management
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Monitor compliance and track certificate renewals across all properties
              </p>
            </div>

            <div className="flex items-center gap-3 w-full lg:w-auto">
              <div className="flex items-center gap-2 p-1 rounded-lg bg-input border border-border">
                <button
                  onClick={() => setViewMode('table')}
                  className={`h-10 px-4 rounded-lg transition-smooth font-caption text-sm ${
                    viewMode === 'table' ?'bg-accent text-white' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name="List" size={18} />
                </button>
                <button
                  onClick={() => setViewMode('calendar')}
                  className={`h-10 px-4 rounded-lg transition-smooth font-caption text-sm ${
                    viewMode === 'calendar' ?'bg-accent text-white' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name="Calendar" size={18} />
                </button>
              </div>

              <Button variant="default" iconName="Download" iconPosition="left" onClick={handleExportData}>
                Export Data
              </Button>
            </div>
          </div>

          <div className="mb-6 md:mb-8">
            <SmartExpiryRadar variant="full" />
          </div>

          <CertificateFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
          />

          {viewMode === 'table' ? (
            <CertificateTable
              certificates={filteredCertificates}
              onViewDetails={handleViewDetails}
              onGenerateReminder={handleGenerateReminder}
              onUploadDocument={handleUploadDocument}
            />
          ) : (
            <CalendarView certificates={filteredCertificates} onDateClick={handleDateClick} />
          )}
        </div>
      </div>
      <CertificateDetailsModal
        isOpen={detailsModal?.isOpen}
        onClose={() => setDetailsModal({ isOpen: false, certificate: null })}
        certificate={detailsModal?.certificate}
      />
      <ReminderEmailModal
        isOpen={reminderModal?.isOpen}
        onClose={() => setReminderModal({ isOpen: false, certificate: null })}
        certificate={reminderModal?.certificate}
      />
      <UploadDocumentModal
        isOpen={uploadModal?.isOpen}
        onClose={() => setUploadModal({ isOpen: false, certificate: null })}
        certificate={uploadModal?.certificate}
      />
      <BulkActionsBar
        selectedCount={selectedCertificates?.length}
        onGenerateReminders={() => console.log('Generate bulk reminders')}
        onExportData={handleExportData}
        onClearSelection={() => setSelectedCertificates([])}
      />
    </>
  );
};

export default SmartCertificateExpiryManagement;